/**
 * @file AuthProvider.tsx
 *
 * Bootstraps admin authentication on load and gates the app behind it. It asks
 * the backend which flow is active (`GET /auth/mode`), then:
 *  - token mode: reads the short-lived token from the iframe URL and exchanges
 *    it (`POST /auth/exchange`) for an in-memory frontend token; on a missing or
 *    rejected token it routes to the unauthorized screen (so a frame refresh,
 *    which drops the in-memory token, lands there, which is expected).
 *  - password mode: probes `GET /ping-authenticated` (cookie); on 401 it renders
 *    the in-app {@link Login} screen, which sets the session cookie and flips the
 *    app to its authenticated state.
 * Either way the bootstrap reads the caller's identity from `/ping-authenticated`
 * and exposes it (plus a {@link logout} action) on the context so the account
 * panel can show who is signed in and end the session. Children render only once
 * authenticated. The context/hook live in {@link ./auth-context}.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { apiFetch, setAuthToken, setUnauthorizedHandler } from '../api/client';
import { messages } from '../i18n/messages';
import { Login } from './Login';
import {
  AuthContext,
  type AdminAuthMode,
  type AuthIdentitySummary,
  type AuthStatus,
} from './auth-context';

const SLT_QUERY_PARAM = 'slt';
const SLT_HEADER = 'X-WSB-Auth-Token';
const UNAUTHORIZED_PAGE = '/403.html';

/** The dynamic auth state held in the provider (the stable `logout` is added on render). */
interface AuthState {
  status: AuthStatus;
  mode: AdminAuthMode | null;
  identity: AuthIdentitySummary | null;
}

function redirect(page: string): void {
  window.location.replace(page);
}

async function fetchMode(): Promise<AdminAuthMode> {
  const response = await apiFetch('/auth/mode');
  if (!response.ok) {
    throw new Error(`auth mode lookup failed: ${response.status}`);
  }
  const body = (await response.json()) as { mode: AdminAuthMode };
  return body.mode;
}

async function exchangeSlt(slt: string): Promise<string | null> {
  const response = await apiFetch('/auth/exchange', {
    method: 'POST',
    headers: { [SLT_HEADER]: slt },
  });
  if (!response.ok) {
    return null;
  }
  const body = (await response.json()) as { token: string };
  return body.token;
}

/**
 * Probes `/ping-authenticated`, which both confirms the session and returns the
 * caller. `ok` drives the authenticated/unauthenticated decision; `identity` is
 * the display summary (absent when the body carries no subject).
 *
 * @returns Whether the session is valid and the caller's display identity.
 */
async function fetchIdentity(): Promise<{
  ok: boolean;
  identity: AuthIdentitySummary | null;
}> {
  const response = await apiFetch('/ping-authenticated');
  if (!response.ok) {
    return { ok: false, identity: null };
  }
  const body = (await response.json()) as {
    identity?: { subject?: string; email?: string };
  };
  const subject = body.identity?.subject;
  return {
    ok: true,
    identity: subject ? { subject, email: body.identity?.email } : null,
  };
}

/**
 * Runs the auth bootstrap once on mount and renders a loading state until it
 * resolves; on success it provides the auth context and renders the app. In
 * password mode an unauthenticated session renders the {@link Login} screen.
 *
 * @param props.children - The authenticated app subtree.
 *
 * @returns The loading screen, the login screen, or the app in the auth context.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    mode: null,
    identity: null,
  });
  const started = useRef(false);
  const modeRef = useRef<AdminAuthMode | null>(null);

  /** Routes an unauthenticated session: login screen (password) or 403 (token). */
  const goUnauthenticated = useCallback((mode: AdminAuthMode): void => {
    if (mode === 'token') {
      redirect(UNAUTHORIZED_PAGE);
      return;
    }
    setState({ status: 'unauthenticated', mode: 'password', identity: null });
  }, []);

  /**
   * Ends the session. Best-effort `POST /auth/logout` clears the httpOnly cookie
   * server-side (password mode); the in-memory token is dropped locally (token
   * mode). Then it routes to the auth entry point: the 403 screen in token mode,
   * the in-app login screen in password mode.
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch {
      // Best-effort: clear client state and route out regardless.
    }
    setAuthToken(null);
    if (modeRef.current === 'token') {
      redirect(UNAUTHORIZED_PAGE);
      return;
    }
    setState({ status: 'unauthenticated', mode: 'password', identity: null });
  }, []);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    const bootstrap = async (): Promise<void> => {
      const mode = await fetchMode();
      modeRef.current = mode;
      setUnauthorizedHandler(() => goUnauthenticated(mode));

      if (mode === 'token') {
        const slt = new URLSearchParams(window.location.search).get(
          SLT_QUERY_PARAM,
        );
        const token = slt ? await exchangeSlt(slt) : null;
        if (!token) {
          redirect(UNAUTHORIZED_PAGE);
          return;
        }
        setAuthToken(token);
      }

      // One probe serves both modes: it validates the session (the cookie in
      // password mode, the just-set bearer token in token mode) and returns the
      // caller identity for the account panel.
      const { ok, identity } = await fetchIdentity();
      if (!ok) {
        goUnauthenticated(mode);
        return;
      }
      setState({ status: 'authenticated', mode, identity });
    };

    bootstrap().catch(() => redirect(UNAUTHORIZED_PAGE));
  }, [goUnauthenticated]);

  if (state.status === 'unauthenticated') {
    return (
      <Login
        onAuthenticated={() => {
          // The cookie is now set; render immediately and backfill the identity.
          setState({ status: 'authenticated', mode: 'password', identity: null });
          void fetchIdentity().then(({ identity }) =>
            setState((prev) =>
              prev.status === 'authenticated' ? { ...prev, identity } : prev,
            ),
          );
        }}
      />
    );
  }

  if (state.status !== 'authenticated') {
    return (
      <div
        role="status"
        style={{ fontFamily: 'system-ui, sans-serif', padding: '2rem' }}
      >
        <FormattedMessage {...messages.loading} />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ ...state, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
